/**
 * Module dependencies.
 */

import {
  Context,
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { County } from 'src/pages/api/counties';
import { District } from 'src/api/get-radar-districts';
import { useRouter } from 'next/router';

/**
 * `Step` type.
 *
 * 1 - User location;
 * 2 - User data;
 * 3 - User data confirmation;
 * 4 - Service unavailable;
 * 5 - Success;
 * 6 - Error;
 * 7 - Payment unavailable.
 */

type Step = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/**
 * `Code` type.
 */

type Code = string | null;

/**
 * `FormData` type.
 */

export type FormData = {
  county: County;
  date: string;
  district: District;
  email?: string;
  hour: string;
  name: string;
  phone: string;
  timeFrame: string;
};

/**
 * `ScheduleProps` type
 */

type ScheduleProps = {
  counties: County[];
  districts: District[] | null;
  formData: FormData;
  isSantanderFlow: boolean;
  processCode: Code;
  reset: () => void;
  setCounties: Dispatch<SetStateAction<County[]>>;
  setProcessCode: Dispatch<SetStateAction<Code>>;
  setStep: Dispatch<SetStateAction<Step>>;
  step: Step;
  updateFormData: (field: keyof FormData, value: string | boolean | District | County) => void;
};

/**
 * `Props` type
 */

type Props = {
  children: ReactNode;
  counties?: County[];
  districts: District[] | null;
  status: number;
};

/**
 * Export `ScheduleContext`.
 */

export const ScheduleContext: Context<ScheduleProps> = createContext({} as ScheduleProps);

/**
 * Export `useSchedule` hook.
 */

export function useSchedule(): ScheduleProps {
  return useContext(ScheduleContext);
}

/**
 * Export `ScheduleProvider` component.
 */

export const ScheduleProvider = ({ children, districts, status }: Props) => {
  const [formData, setFormData] = useState<FormData>({
    county: {
      countyCode: '',
      countyName: '',
      districtCode: '',
      districtName: ''
    },
    date: '',
    district: {
      districtCode: '',
      districtName: ''
    },
    email: '',
    hour: '',
    name: '',
    phone: '',
    timeFrame: ''
  });

  const [counties, setCounties] = useState<County[]>([]);
  const [step, setStep] = useState<Step>(1);
  const [processCode, setProcessCode] = useState<Code>(null);
  const [isSantanderFlow, setIsSantanderFlow] = useState<boolean>(false);

  const router = useRouter();

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    setIsSantanderFlow(router.query.canal === 'santander');
  }, [router.isReady, router.query.canal]);

  const updateFormData = (field: keyof FormData, value: string | boolean | District | County) => {
    setFormData(prevState => ({
      ...prevState,
      [field]: value
    }));
  };

  const value = useMemo(() => {
    return {
      counties,
      districts,
      formData,
      isSantanderFlow,
      processCode,
      setCounties,
      setProcessCode,
      setStep,
      step,
      updateFormData
    };
  }, [counties, districts, formData, isSantanderFlow, processCode, step]);

  const reset = useCallback(() => {
    setFormData({
      county: {
        countyCode: '',
        countyName: '',
        districtCode: '',
        districtName: ''
      },
      date: '',
      district: {
        districtCode: '',
        districtName: ''
      },
      email: '',
      hour: '',
      name: '',
      phone: '',
      timeFrame: ''
    });

    setProcessCode(null);
    setStep(1);
  }, []);

  useEffect(() => {
    if (status === 503) {
      setStep(4);
    }
  }, [status]);

  return <ScheduleContext.Provider value={{ ...value, reset }}>{children}</ScheduleContext.Provider>;
};
